// src/utils/actionsFnct.ts — SCE Issues
// La vie des actions d'une FNC-T (chantier FNC-T, lot F3) : ajouter, clore,
// reporter, modifier. Pur, testable : rang suivant, contrôles, textes de
// l'historique (SCE_FNCT_Historique), commentaire de l'action.
// Maquette : https://claude.ai/artifact/LCfQEHVXcRmKcA6CSDy6ix
//
// Règles reprises de la base Excel : UN report gardé (« Renewed due date »),
// statut calculé par les dates (utils/fnct.statutAction). Le motif d'un report
// et le commentaire d'une clôture rejoignent le commentaire de l'action, comme
// la colonne « Comments & Attachments » de la base.

import type { ActionFnct, CategorieAction, Jour } from '@/types/fnct.types'
import { LIBELLES_CATEGORIE_ACTION } from '@/types/fnct.types'
import { idAction, joursEntre, jourLisible, memePersonne } from './fnct'
import { decalerJours, rangDansSemaine } from './calendrier'

/** Rang de la prochaine action d'une FNC-T : la plus grande + 1 (`…-A04` → 5). */
export function prochainRangAction(actions: readonly Pick<ActionFnct, 'id' | 'fnct'>[], numero: string): number {
  let max = 0
  for (const a of actions) {
    if (a.fnct.toUpperCase() !== numero.toUpperCase()) continue
    const m = /-A(\d+)$/i.exec(a.id)
    if (m && Number(m[1]) > max) max = Number(m[1])
  }
  return max + 1
}

export function prochainIdAction(actions: readonly Pick<ActionFnct, 'id' | 'fnct'>[], numero: string): string {
  return idAction(numero, prochainRangAction(actions, numero))
}

/** Une ligne de plus au commentaire de l'action. */
export function ajouterAuCommentaire(existant: string, ligne: string): string {
  const l = ligne.trim()
  if (!l) return existant
  return existant.trim() ? `${existant.trim()}\n${l}` : l
}

// ─── Contrôles (null = c'est bon) ───

export interface SaisieAction {
  categorie: CategorieAction
  description: string
  responsable: string
  /** Adresse du responsable, choisi dans l'annuaire ; absente : retrouvée à l'écriture. */
  responsableUPN?: string
  service: string
  echeance: Jour | null
}

export function erreurSaisieAction(s: SaisieAction): string | null {
  if (!s.description.trim()) return 'Décrivez l’action.'
  if (!s.responsable.trim()) return 'Choisissez un responsable.'
  if (!s.echeance) return 'Choisissez une échéance.'
  return null
}

export function erreurCloture(a: Pick<ActionFnct, 'cloture' | 'ouverture'>, cloture: Jour | null, aujourdhui: Jour): string | null {
  if (a.cloture) return `Action déjà close le ${jourLisible(a.cloture)}.`
  if (!cloture) return 'Choisissez la date de clôture.'
  if (cloture > aujourdhui) return 'Une action ne se clôt pas dans le futur.'
  if (a.ouverture && cloture < a.ouverture) return `Close avant d’être ouverte (le ${jourLisible(a.ouverture)}) ?`
  return null
}

/**
 * Un report : nouvelle échéance après l'échéance en vigueur, motif obligatoire.
 * Autant de reports que nécessaire depuis le 30/10 (Guillaume) : la base ne garde
 * qu'UNE date reportée (la dernière remplace la précédente), chaque motif reste
 * dans le commentaire de l'action.
 */
export function erreurReport(a: Pick<ActionFnct, 'cloture' | 'echeance' | 'echeanceReportee'>, nouvelle: Jour | null, motif: string): string | null {
  if (a.cloture) return 'Une action close ne se reporte pas.'
  if (!nouvelle) return 'Choisissez la nouvelle échéance.'
  const limite = a.echeanceReportee ?? a.echeance
  if (limite && nouvelle <= limite) {
    return `La nouvelle échéance doit suivre l’échéance ${a.echeanceReportee ? 'reportée' : 'initiale'} (${jourLisible(limite)}).`
  }
  if (!motif.trim()) return 'Dites pourquoi l’action est reportée.'
  return null
}

/**
 * Combien de fois l'action a été reportée : ses lignes « postponed to » (ligneReport),
 * au moins une si elle porte une date reportée (une action reprise de l'Excel n'a pas
 * ces lignes).
 */
export function nombreDeReports(a: Pick<ActionFnct, 'echeanceReportee' | 'commentaire'>): number {
  const n = (a.commentaire.match(/— postponed to \d{2}\/\d{2}\/\d{4}:/g) ?? []).length
  return Math.max(n, a.echeanceReportee ? 1 : 0)
}

// ─── Confier une action à quelqu'un d'autre (30/10) ───

/** À qui l'on confie : choisi dans l'annuaire. */
export interface Destinataire {
  nom: string
  upn: string
  service: string
}

/**
 * Confier une action (Guillaume, 30/10 : « commentaire obligatoire ») : une AUTRE
 * personne, et le motif. Même règle pour « Confier », pour « Modifier » quand le
 * responsable change, et pour « Réattribuer ses actions » de la revue.
 */
export function erreurConfier(
  a: Pick<ActionFnct, 'cloture' | 'responsable' | 'responsableUPN'>,
  vers: Pick<Destinataire, 'nom' | 'upn'> | null,
  motif: string,
): string | null {
  if (a.cloture) return 'Une action close ne se confie plus.'
  if (!vers || !vers.nom.trim()) return 'Choisissez à qui confier l’action.'
  const memeAdresse = !!vers.upn && !!a.responsableUPN && vers.upn.trim().toLowerCase() === a.responsableUPN.trim().toLowerCase()
  if (memeAdresse || memePersonne(vers.nom, a.responsable)) return `L’action est déjà à ${a.responsable.trim()}.`
  if (!motif.trim()) return 'Dites pourquoi l’action change de mains.'
  return null
}

/** Ligne du commentaire de l'action (anglais, comme celle d'un report). */
export function ligneConfiee(de: string, vers: string, motif: string, aujourdhui: Jour): string {
  return `${jourLisible(aujourdhui)} — reassigned to ${vers.trim()} (was ${de.trim() || 'nobody'}): ${motif.trim()}`
}

// ─── Textes de l'historique ───

function categorie(c: CategorieAction | ''): string {
  return c ? `${c} (${LIBELLES_CATEGORIE_ACTION[c].fr.toLowerCase()})` : 'sans catégorie'
}

export function texteAjout(a: Pick<ActionFnct, 'categorie' | 'description' | 'responsable' | 'echeance'>): string {
  return `${a.description} — ${categorie(a.categorie)}, ${a.responsable}, échéance ${jourLisible(a.echeance)}`
}

export function texteCloture(a: Pick<ActionFnct, 'description' | 'echeance' | 'echeanceReportee'>, cloture: Jour, commentaire: string): string {
  const limite = a.echeanceReportee ?? a.echeance
  const temps = limite ? (cloture <= limite ? 'à temps' : 'en retard') : ''
  return [`${a.description} — close le ${jourLisible(cloture)}${temps ? `, ${temps}` : ''}`, commentaire.trim()].filter(Boolean).join('\n')
}

export function texteReport(a: Pick<ActionFnct, 'description' | 'echeance' | 'echeanceReportee'>, nouvelle: Jour, motif: string): string {
  const avant = a.echeanceReportee ?? a.echeance
  return `${a.description} — ${jourLisible(avant)} → ${jourLisible(nouvelle)}\n${motif.trim()}`
}

/**
 * « Modifier » une action déjà reportée (30/10) : la date du formulaire est
 * l'échéance EN VIGUEUR, la reportée — celle que montrent toutes les listes ;
 * l'échéance initiale reste la trace du report. Avant, le formulaire montrait et
 * réécrivait l'initiale : on croyait déplacer l'échéance, rien ne bougeait.
 */
export function echeancesApresModification(
  a: Pick<ActionFnct, 'echeance' | 'echeanceReportee'>,
  saisie: Jour | null,
): Pick<ActionFnct, 'echeance' | 'echeanceReportee'> {
  return a.echeanceReportee ? { echeance: a.echeance, echeanceReportee: saisie } : { echeance: saisie, echeanceReportee: null }
}

/** Une action reportée garde une échéance en vigueur APRÈS l'initiale (même règle que le report). */
export function erreurModificationEcheance(a: Pick<ActionFnct, 'echeance' | 'echeanceReportee'>, saisie: Jour | null): string | null {
  if (a.echeanceReportee && a.echeance && saisie && saisie <= a.echeance) {
    return `Action reportée : l’échéance doit suivre l’échéance initiale (${jourLisible(a.echeance)}).`
  }
  return null
}

/** Ligne du commentaire de l'action pour un report (anglais : elle peut partir dans le rapport). */
export function ligneReport(nouvelle: Jour, motif: string, aujourdhui: Jour): string {
  return `${jourLisible(aujourdhui)} — postponed to ${jourLisible(nouvelle)}: ${motif.trim()}`
}

// ─── Droits ───

/**
 * Clore ou reporter une action : son responsable, ou qui gère les FNC-T
 * (revue : coordinateurs, AQ/CQ, administrateur — config/fnct.config).
 */
export function peutAgirSurAction(nomUtilisateur: string, a: Pick<ActionFnct, 'responsable'>, gestionnaire: boolean): boolean {
  return gestionnaire || memePersonne(nomUtilisateur, a.responsable)
}

// ─── Le plan d'actions (piste A + frise, 06/10) ───

/** « Mathieu Mourolin » → « M. Mourolin » (colonne Qui). */
export function nomCourt(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter(Boolean)
  if (mots.length < 2) return nom.trim()
  return `${(mots[0] ?? '').charAt(0)}. ${mots.slice(1).join(' ')}`
}

export interface PourQuand {
  /** « 26/10 · dans 20 j », « 26/10 · 3 j de retard », « close le 14/10 ». */
  texte: string
  /** « reportée du 26/10 ». */
  precision?: string
  retard: boolean
}

function jourCourt(j: Jour | null): string {
  return j ? jourLisible(j).slice(0, 5) : '—'
}

export function pourQuand(a: Pick<ActionFnct, 'echeance' | 'echeanceReportee' | 'cloture'> & Partial<Pick<ActionFnct, 'commentaire'>>, aujourdhui: Jour): PourQuand {
  if (a.cloture) return { texte: `close le ${jourCourt(a.cloture)}`, retard: false }
  const echeance = a.echeanceReportee ?? a.echeance
  if (!echeance) return { texte: 'sans échéance', retard: false }
  const n = joursEntre(aujourdhui, echeance)
  const quand = n > 0 ? `dans ${n} j` : n === 0 ? 'aujourd’hui' : `${-n} j de retard`
  // Plusieurs reports (30/10) : on le dit, avec la date prévue au départ.
  const reports = a.echeanceReportee ? nombreDeReports({ echeanceReportee: a.echeanceReportee, commentaire: a.commentaire ?? '' }) : 0
  return {
    texte: `${jourCourt(echeance)} · ${quand}`,
    precision: reports > 1 ? `reportée ${reports} fois, prévue le ${jourCourt(a.echeance)}` : reports === 1 ? `reportée du ${jourCourt(a.echeance)}` : undefined,
    retard: n < 0,
  }
}

/** Les actions ouvertes par échéance (la plus proche d'abord), puis les closes, les plus récentes d'abord. */
export function trierPlan<T extends Pick<ActionFnct, 'echeance' | 'echeanceReportee' | 'cloture' | 'id'>>(actions: readonly T[]): T[] {
  const ouvertes = actions.filter((a) => !a.cloture)
  const closes = actions.filter((a) => a.cloture)
  const cle = (a: T): string => a.echeanceReportee ?? a.echeance ?? '9999'
  return [
    ...ouvertes.sort((x, y) => cle(x).localeCompare(cle(y)) || x.id.localeCompare(y.id)),
    ...closes.sort((x, y) => (y.cloture ?? '').localeCompare(x.cloture ?? '') || x.id.localeCompare(y.id)),
  ]
}

export interface LigneFrise {
  /** Positions en % de la largeur de la frise. */
  debut: number
  fin: number
  /** Échéance initiale quand elle a été reportée. */
  initiale?: number
  /**
   * Action close : son échéance en vigueur (reportée ou non), qui n'est pas le bout
   * de la barre — sans elle, une échéance reportée disparaissait à la clôture (28/10).
   */
  echeance?: number
  /** Close après son échéance en vigueur. */
  closeEnRetard?: boolean
  /**
   * Le retard, hachuré (12/10) : de l'échéance en vigueur à aujourd'hui pour
   * une action ouverte, ou à sa clôture pour une action close en retard — le
   * retard rattrapé reste lisible.
   */
  retard?: { debut: number; fin: number }
  etat: 'close' | 'retard' | 'reportee' | 'ouverte'
}

export interface Frise {
  debut: Jour
  fin: Jour
  /** Position d'aujourd'hui, ou null quand toutes les actions sont closes : la frise raconte alors une histoire finie. */
  aujourdhui: number | null
  lignes: LigneFrise[]
}

type ActionDatee = Pick<ActionFnct, 'ouverture' | 'echeance' | 'echeanceReportee' | 'cloture'>

/**
 * De la plus ancienne ouverture à la plus lointaine échéance. Aujourd'hui n'y
 * entre que s'il reste une action ouverte (28/10, Guillaume) : sur une FNC-T close
 * depuis deux ans, il écrasait toute la frise à gauche pour rien.
 */
function bornesFrise(actions: readonly ActionDatee[], aujourdhui: Jour): { debut: Jour; fin: Jour; enCours: boolean } {
  const enCours = actions.length === 0 || actions.some((a) => !a.cloture)
  const jours: Jour[] = enCours ? [aujourdhui] : []
  for (const a of actions) for (const j of [a.ouverture, a.echeance, a.echeanceReportee, a.cloture]) if (j) jours.push(j)
  if (jours.length === 0) jours.push(aujourdhui)
  jours.sort()
  return { debut: jours[0] ?? aujourdhui, fin: jours[jours.length - 1] ?? aujourdhui, enCours }
}

/**
 * La frise : de la plus ancienne ouverture à la plus lointaine échéance
 * (aujourd'hui compris tant qu'une action est ouverte). Une barre par action, de
 * son ouverture à sa clôture, ou à son échéance en vigueur si elle est ouverte.
 */
export function geometrieFrise(actions: readonly ActionDatee[], aujourdhui: Jour): Frise {
  const { debut, fin, enCours } = bornesFrise(actions, aujourdhui)
  const total = Math.max(1, joursEntre(debut, fin))
  const pos = (j: Jour): number => Math.max(0, Math.min(100, Math.round((joursEntre(debut, j) / total) * 1000) / 10))
  return {
    debut,
    fin,
    aujourdhui: enCours ? pos(aujourdhui) : null,
    lignes: actions.map((a) => {
      const echeance = a.echeanceReportee ?? a.echeance
      const bout = a.cloture ?? echeance ?? aujourdhui
      const etat: LigneFrise['etat'] = a.cloture
        ? 'close'
        : echeance && echeance < aujourdhui
          ? 'retard'
          : a.echeanceReportee
            ? 'reportee'
            : 'ouverte'
      const retard =
        echeance && a.cloture && a.cloture > echeance
          ? { debut: pos(echeance), fin: pos(a.cloture) }
          : echeance && !a.cloture && echeance < aujourdhui
            ? { debut: pos(echeance), fin: pos(aujourdhui) }
            : null
      return {
        debut: pos(a.ouverture ?? debut),
        fin: pos(bout),
        ...(a.echeanceReportee && a.echeance ? { initiale: pos(a.echeance) } : {}),
        ...(a.cloture && echeance ? { echeance: pos(echeance) } : {}),
        ...(a.cloture && echeance && a.cloture > echeance ? { closeEnRetard: true } : {}),
        ...(retard ? { retard } : {}),
        etat,
      }
    }),
  }
}

// ─── L'axe : quelques repères pour situer le temps (28/10) ───

export interface RepereFrise {
  jour: Jour
  libelle: string
  /** % de la largeur de la frise. */
  position: number
}

export interface AxeFrise {
  reperes: RepereFrise[]
  /** Position d'aujourd'hui (son libellé remplace les repères trop proches), ou null. */
  aujourdhui: number | null
}

/** Du plus fin au plus large : on garde le plus fin qui tient en quelques repères. */
const NIVEAUX_REPERES = ['jours', 'lundis', 'mois', 'trimestres', 'annees'] as const
type NiveauRepere = (typeof NIVEAUX_REPERES)[number]

const NOMS_MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
/** Assez pour situer le temps, pas assez pour encombrer (Guillaume, 28/10). */
export const MAX_REPERES_FRISE = 6
/** Un libellé plus à droite déborderait de la frise. */
const POSITION_MAX_REPERE = 92
/** Un repère plus proche d'aujourd'hui serait caché par son libellé. */
const ECART_AUJOURDHUI = 10

function estRepere(niveau: NiveauRepere, d: Jour): boolean {
  const jour = Number(d.slice(8, 10))
  const mois = Number(d.slice(5, 7))
  switch (niveau) {
    case 'jours':
      return true
    case 'lundis':
      return rangDansSemaine(d) === 0
    case 'mois':
      return jour === 1
    case 'trimestres':
      return jour === 1 && (mois - 1) % 3 === 0
    case 'annees':
      return jour === 1 && mois === 1
    default: {
      const x: never = niveau
      return x
    }
  }
}

/**
 * Les repères d'une frise de `debut` à `fin` : les débuts d'année, de trimestre ou
 * de mois, les lundis ou les jours — le niveau le plus fin qui tient en
 * {@link MAX_REPERES_FRISE} repères. Le premier mois porte l'année, comme chaque
 * janvier. Aujourd'hui (s'il est sur la frise) a son propre libellé : les repères
 * trop proches s'effacent devant lui.
 */
export function axeFrise(debut: Jour, fin: Jour, aujourdhui: Jour | null = null): AxeFrise {
  const total = Math.max(1, joursEntre(debut, fin))
  const pos = (j: Jour): number => Math.round((joursEntre(debut, j) / total) * 1000) / 10
  const parNiveau = new Map<NiveauRepere, Jour[]>(NIVEAUX_REPERES.map((n) => [n, []]))
  for (let d = debut, garde = 0; d <= fin && garde < 5000; d = decalerJours(d, 1), garde += 1) {
    for (const n of NIVEAUX_REPERES) if (estRepere(n, d)) parNiveau.get(n)?.push(d)
  }
  let niveau: NiveauRepere = 'annees'
  for (const n of NIVEAUX_REPERES) {
    if ((parNiveau.get(n)?.length ?? 0) <= MAX_REPERES_FRISE) {
      niveau = n
      break
    }
  }
  let jours = parNiveau.get(niveau) ?? []
  // Un seul repère ne situe rien : le niveau plus fin, éclairci.
  const rang = NIVEAUX_REPERES.indexOf(niveau)
  if (jours.length < 2 && rang > 0) {
    const fins = parNiveau.get(NIVEAUX_REPERES[rang - 1] ?? 'jours') ?? []
    const pas = Math.max(1, Math.ceil(fins.length / MAX_REPERES_FRISE))
    jours = fins.filter((_, i) => i % pas === 0)
    niveau = NIVEAUX_REPERES[rang - 1] ?? niveau
  }
  // Plus d'années que de repères permis (une FNC-T de dix ans) : une sur deux, sur trois…
  if (jours.length > MAX_REPERES_FRISE) {
    const pas = Math.ceil(jours.length / MAX_REPERES_FRISE)
    jours = jours.filter((_, i) => i % pas === 0)
  }
  const posAujourdhui = aujourdhui ? pos(aujourdhui) : null
  const reperes: RepereFrise[] = []
  for (const j of jours) {
    const position = pos(j)
    if (position > POSITION_MAX_REPERE) continue
    if (posAujourdhui !== null && Math.abs(position - posAujourdhui) < ECART_AUJOURDHUI) continue
    const mois = Number(j.slice(5, 7))
    const annee = j.slice(0, 4)
    const libelle =
      niveau === 'annees'
        ? annee
        : niveau === 'mois' || niveau === 'trimestres'
          ? reperes.length === 0 || mois === 1
            ? `${NOMS_MOIS[mois - 1] ?? ''} ${annee}`
            : (NOMS_MOIS[mois - 1] ?? '')
          : `${j.slice(8, 10)}/${j.slice(5, 7)}`
    reperes.push({ jour: j, libelle, position })
  }
  return { reperes, aujourdhui: posAujourdhui }
}

// ─── Ce que disent les points de la frise, au survol (28/10) ───

export interface TextePoint {
  titre: string
  lignes: string[]
}

export interface TextesPointsFrise {
  /** Début de la barre. */
  ouverture?: TextePoint
  /** Échéance initiale, quand l'action a été reportée. */
  initiale?: TextePoint
  /** Action close : son échéance en vigueur. */
  echeance?: TextePoint
  /** Bout de la barre : la clôture, ou l'échéance en vigueur. */
  fin: TextePoint
}

function tronquer(texte: string, max: number): string {
  return texte.length > max ? `${texte.slice(0, max - 1).trimEnd()}…` : texte
}

function lignesCommentaire(a: Pick<ActionFnct, 'commentaire'>): string[] {
  return a.commentaire.split('\n').map((l) => l.trim()).filter(Boolean)
}

/** Un texte par point de la barre : ce qui s'est passé CE jour-là, et ce qu'il en résulte. */
export function textesPointsFrise(a: ActionFnct, aujourdhui: Jour): TextesPointsFrise {
  const echeance = a.echeanceReportee ?? a.echeance
  const commentaires = lignesCommentaire(a)
  const textes: TextesPointsFrise = { fin: { titre: '', lignes: [] } }
  if (a.ouverture) {
    textes.ouverture = {
      titre: `Ouverte le ${jourLisible(a.ouverture)}`,
      lignes: [
        `${a.responsable.trim() || 'Sans responsable'}${a.service.trim() ? ` · ${a.service.trim()}` : ''}`,
        ...(a.categorie ? [`${a.categorie} · ${LIBELLES_CATEGORIE_ACTION[a.categorie].fr}`] : []),
      ],
    }
  }
  if (a.echeanceReportee && a.echeance) {
    const motif = [...commentaires].reverse().find((l) => /postponed/i.test(l))
    textes.initiale = {
      titre: `Échéance initiale : ${jourLisible(a.echeance)}`,
      lignes: [
        `Reportée au ${jourLisible(a.echeanceReportee)}${nombreDeReports(a) > 1 ? ` · ${nombreDeReports(a)} reports` : ''}`,
        ...(motif ? [`« ${tronquer(motif, 90)} »`] : []),
      ],
    }
  }
  if (a.cloture && echeance) {
    const ecart = joursEntre(echeance, a.cloture)
    textes.echeance = {
      titre: `${a.echeanceReportee ? 'Échéance reportée' : 'Échéance'} : ${jourLisible(echeance)}`,
      lignes: [ecart > 0 ? `Close ${ecart} j après, le ${jourLisible(a.cloture)}` : ecart === 0 ? 'Close le jour même' : `Close ${-ecart} j avant, le ${jourLisible(a.cloture)}`],
    }
  }
  if (a.cloture) {
    const ecart = echeance ? joursEntre(echeance, a.cloture) : 0
    const dernier = commentaires.at(-1)
    textes.fin = {
      titre: `Close le ${jourLisible(a.cloture)}`,
      lignes: [
        !echeance ? 'Sans échéance' : ecart > 0 ? `${ecart} j après l’échéance du ${jourLisible(echeance)}` : `À temps (échéance du ${jourLisible(echeance)})`,
        ...(a.ouverture ? [`Traitée en ${joursEntre(a.ouverture, a.cloture)} j`] : []),
        ...(dernier ? [`« ${tronquer(dernier, 90)} »`] : []),
      ],
    }
  } else {
    const depuis = a.ouverture ? [`Ouverte depuis ${joursEntre(a.ouverture, aujourdhui)} j`] : []
    if (echeance) {
      const n = joursEntre(aujourdhui, echeance)
      textes.fin = {
        titre: `${a.echeanceReportee ? 'Échéance reportée' : 'Échéance'} : ${jourLisible(echeance)}`,
        lignes: [n > 0 ? `Dans ${n} j` : n === 0 ? 'Aujourd’hui' : `En retard de ${-n} j`, ...depuis],
      }
    } else textes.fin = { titre: 'Sans échéance', lignes: depuis }
  }
  return textes
}

/** Tout ce que dit la ligne, d'un trait : pour les lecteurs d'écran (les points ne se survolent qu'à la souris). */
export function resumeActionFrise(a: ActionFnct, aujourdhui: Jour): string {
  const t = textesPointsFrise(a, aujourdhui)
  return [a.description.trim(), t.ouverture, t.initiale, t.echeance, t.fin]
    .map((x) => (typeof x === 'string' ? x : x ? [x.titre, ...x.lignes].join(', ') : ''))
    .filter(Boolean)
    .join('. ')
}
